import { supabase } from '../supabaseClient';

export const agentClientsService = {
    async getByAgentNpn(npn) {
        const { data, error } = await supabase
            .from('agent_clients')
            .select('*')
            .eq('agent_npn', npn)
            .order('created_at', { ascending: false })
            .limit(5000);
        if (error) throw error;
        return data || [];
    },

    async create(client) {
        const { data, error } = await supabase
            .from('agent_clients')
            .insert([client])
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async insertBatch(clients) {
        if (!clients || clients.length === 0) return [];
        const { data, error } = await supabase
            .from('agent_clients')
            .insert(clients)
            .select();
        if (error) throw error;
        return data;
    },

    async upsertBatch(clients) {
        if (!clients || clients.length === 0) return [];
        const { data, error } = await supabase
            .from('agent_clients')
            .upsert(clients, {
                onConflict: 'policy_number,agent_npn',
                ignoreDuplicates: false
            })
            .select();
        if (error) throw error;
        return data;
    },

    async update(id, updates) {
        const { data, error } = await supabase
            .from('agent_clients')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('agent_clients')
            .delete()
            .eq('id', id);
        if (error) throw error;
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('agent_clients')
            .select('*')
            .eq('id', id)
            .single();
        if (error) throw error;
        return data;
    },

    async updateStatus(id, newStatus) {
        const { data, error } = await supabase
            .from('agent_clients')
            .update({ status: newStatus })
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async getStats(npn) {
        const { data, error } = await supabase
            .from('agent_clients')
            .select('*')
            .eq('agent_npn', npn)
            .limit(5000);
        if (error) throw error;

        const now = new Date();
        const thisMonth = data?.filter(c => {
            const date = new Date(c.created_at);
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        }).length || 0;

        return {
            total: data?.length || 0,
            active: data?.filter(c => c.status === 'active').length || 0,
            newThisMonth: thisMonth
        };
    },

    // Obtener todos los clientes de una agencia (via RPC - sin limite de filas)
    async getByAgency(agencyId) {
        const { data, error } = await supabase
            .rpc('get_agency_clients', { p_agency_id: agencyId });

        if (error) throw error;

        // RPC returns JSON (single value, not rows), so data is the array directly
        return Array.isArray(data) ? data : [];
    },

    // Obtener estadísticas detalladas para el dashboard
    async getDetailedStats(npn) {
        const { data, error } = await supabase
            .from('agent_clients')
            .select('*')
            .eq('agent_npn', npn)
            .limit(5000);
        if (error) throw error;

        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        // Pólizas activas
        const activePolicies = data?.filter(c => c.status === 'active') || [];

        // Miembros cubiertos (suma de member_count de pólizas activas)
        const totalMembers = activePolicies.reduce((sum, client) => {
            return sum + (client.member_count || 1);
        }, 0);

        // Distribución por carrier
        const carrierDistribution = {};
        activePolicies.forEach(client => {
            const carrier = client.carrier || 'Sin Carrier';
            carrierDistribution[carrier] = (carrierDistribution[carrier] || 0) + 1;
        });

        return {
            activePoliciesCount: activePolicies.length,
            totalMembers: totalMembers,
            carrierDistribution: carrierDistribution,
            totalClients: data?.length || 0,
            inactiveClients: data?.filter(c => c.status === 'inactive').length || 0
        };
    },

    // Obtener datos para gráfico de crecimiento mensual
    async getMonthlyGrowth(npn, months = 6) {
        const { data, error } = await supabase
            .from('agent_clients')
            .select('created_at, status')
            .eq('agent_npn', npn)
            .order('created_at', { ascending: true })
            .limit(5000);
        if (error) throw error;

        const now = new Date();
        const monthlyData = [];

        for (let i = months - 1; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const nextDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

            const monthClients = data?.filter(c => {
                const clientDate = new Date(c.created_at);
                return clientDate >= date && clientDate < nextDate;
            }) || [];

            monthlyData.push({
                month: date.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
                count: monthClients.length,
                active: monthClients.filter(c => c.status === 'active').length,
                inactive: monthClients.filter(c => c.status === 'inactive').length
            });
        }

        return monthlyData;
    }
};
